import {
  forwardRef,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { clsx, type ClassValue } from "clsx";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import SectionSepratorHeader from "../../components/ui/section-seprator-header";
import AnimatedSpanEl from "../../components/core/animated-span-el";
import { SVGConnector, type SVGConnectorHandle } from "../../components/core/connector-svg";

interface CardProps extends PropsWithChildren {
  index: number;
  title: ReactNode;
  description: ReactNode;
  className?: ClassValue | ClassValue[];
}
const OurProcess = () => {
  const PATH_A = `M 10.716 56.236 C 10.716 56.236 38.876 9.087 88.573 3.406 C 138.271 -2.276 172.885 11.384 233.016 57.669 `;
  const PATH_B = `M 95.993 11.704 C 43.708 8.765 57.558 98.411 77.749 93.631 C 97.94 88.852 48.295 23.589 19.858 39.508 C -8.579 55.425 10.736 95.485 15.297 103.641 C 19.859 111.796 46.874 150.721 92.134 170.369`;
  const mainContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointA1 = useRef<HTMLDivElement | null>(null);
  const pointA2 = useRef<HTMLDivElement | null>(null);
  const pointB1 = useRef<HTMLDivElement | null>(null);
  const pointB2 = useRef<HTMLDivElement | null>(null);
  const connectorA = useRef<SVGConnectorHandle | null>(null);
  const connectorB = useRef<SVGConnectorHandle | null>(null);
  const spansG1Ref = useRef<(HTMLDivElement | null)[]>([]);
  const spansG2Ref = useRef<(HTMLDivElement | null)[]>([]);
  const spansG3Ref = useRef<(HTMLDivElement | null)[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  useGSAP(
    () => {
      const containerEl = mainContainerRef?.current;
      const connectorAEl = connectorA?.current;
      const connectorBEl = connectorB?.current;
      const cards = cardsRef?.current;
      const g1 = spansG1Ref?.current;
      const g2 = spansG2Ref?.current;
      const g3 = spansG3Ref?.current;

      if (!containerEl || !cards || !g1 || !g2 || !g3) return;

      const mm = gsap.matchMedia();

      mm.add("(min-width: 768px)", () => {
        if (!connectorBEl || !connectorAEl) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: containerEl,
            start: "top top",
            end: "+=600%",
            scrub: true,
            pin: true,
            pinSpacing: true,
            invalidateOnRefresh: true,
          },
        });

        tl.to(cards[0], { opacity: 1, duration: 3 }, ">");
        g1.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));
        tl.to(
          { p: 0 },
          {
            p: 1,
            onUpdate: function () {
              connectorA.current?.setProgress(this.targets()[0].p);
            },
            duration: 7,
          },
          ">",
        );
        tl.to(cards[1], { opacity: 1, duration: 3 }, ">");
        g2.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));
        tl.to(
          { p: 0 },
          {
            p: 1,
            onUpdate: function () {
              connectorB.current?.setProgress(this.targets()[0].p);
            },
            duration: 7,
          },
          ">",
        );
        tl.to(cards[2], { opacity: 1, duration: 3 }, ">");
        g3.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));
        tl.add(
          gsap.to(
            { p: 0 },
            {
              p: 1,
              duration: tl.duration(),
              onUpdate: function () {
                connectorRef.current?.setProgress(this.targets()[0].p);
              },
            },
          ),
          0,
        );
      });

      mm.add("(max-width: 767px)", () => {
        const gridContainer = containerRef.current;
        if (!gridContainer) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: gridContainer,
            start: "top top",
            end: "+=400%",
            scrub: true,
            pin: true,
            pinSpacing: true,
            invalidateOnRefresh: true,
          },
        });

        tl.to(cards[0], { opacity: 1, duration: 3 }, ">");
        g1.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));

        // Connectors for mobile
        tl.to(
          { p: 0 },
          {
            p: 1,
            onUpdate: function () {
              connectorA.current?.setProgress(this.targets()[0].p);
            },
            duration: 7,
          },
          ">",
        );

        tl.to(cards[1], { opacity: 1, duration: 3 }, ">");
        g2.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));

        tl.to(
          { p: 0 },
          {
            p: 1,
            onUpdate: function () {
              connectorB.current?.setProgress(this.targets()[0].p);
            },
            duration: 7,
          },
          ">",
        );

        tl.to(cards[2], { opacity: 1, duration: 3 }, ">");
        g3.forEach((el) => tl.to(el, { translateY: "-50%" }, ">"));
      });

      return () => mm.revert();
    },
    {
      dependencies: [
        mainContainerRef,
        connectorA,
        connectorB,
        spansG1Ref,
        spansG2Ref,
        spansG3Ref,
        cardsRef,
      ],
    },
  );
  let g1Index = 0;
  let g2Index = 0;
  let g3Index = 0;

  // const PATH = `M 157 -48 C 99 65 109 107 138 234 C 355 367 303 36 514 91 C 851 198 530 571 423 376 C 449 127 754 357 935 354`;
  // const pointA1Ref = useRef<HTMLDivElement | null>(null);
  // const pointA2Ref = useRef<HTMLDivElement | null>(null);
  const connectorRef = useRef<SVGConnectorHandle | null>(null);
  return (
    <>
      <div
        ref={mainContainerRef}
        className="w-full pb-20 md:pb-32 min-h-screen md:h-screen flex flex-col items-center justify-center relative bg-[#111212] overflow-hidden"
      >
        <div className="w-full flex items-start text-white justify-start">
          <SectionSepratorHeader
            className="mb-8 md:mb-12"
            title="TTAILOURING SOUTIONS FOR"
            subtitle={
              <>
                Forged in <span className="text-[#4991b8] italic">Trust</span>
              </>
            }
            description="Reliability at scale. Here is what leading industrial properties and brands say about our identifies."
          ></SectionSepratorHeader>
        </div>
        {/* <div ref={pointA1Ref} className="absolute w-4 h-4 left-0 top-0"></div>
      <div
        ref={pointA2Ref}
        className="absolute w-4 h-4 right-0 bottom-1/6"
      ></div>
      <SVGConnector
        zIndex={0}
        containerRef={mainContainerRef}
        fromRef={pointA1Ref}
        toRef={pointA2Ref}
        baseColor="#e0e0e0"
        color="#111212"
        path={PATH}
        pathFrom={{
          x: 157,
          y: -48,
        }}
        pathTo={{
          x: 934,
          y: 354,
        }}
        strokeWidth={29}
        ref={connectorRef}
      /> */}

        <div
          ref={containerRef}
          className="w-full grid gap-10 md:gap-4 h-auto pt-7 grid-cols-1 md:grid-cols-3 px-4 md:px-6 justify-center items-center relative"
        >
          {ourProcess.map((a, i) => {
            const spanRef =
              i === 0 ? spansG1Ref : i === 1 ? spansG2Ref : spansG3Ref;
            const getIndex =
              i === 0
                ? () => g1Index++
                : i === 1
                  ? () => g2Index++
                  : () => g3Index++;
            return (
              <Card
                index={a.index}
                ref={(el) => {
                  if (cardsRef?.current) {
                    cardsRef.current[i] = el;
                  }
                }}
                title={
                  <div className="flex flex-wrap w-full h-auto">
                    {a.title?.split(" ")?.map((a, i) => {
                      const indx = getIndex();
                      return (
                        <AnimatedSpanEl
                          key={`items-item-${a}-${i}`}
                          ref={(el) => {
                            if (spanRef?.current) {
                              spanRef.current[indx] = el;
                            }
                          }}
                        >
                          {a}&nbsp;
                        </AnimatedSpanEl>
                      );
                    })}
                  </div>
                }
                description={
                  <div className="flex flex-wrap w-full h-auto">
                    {a.description?.split(" ")?.map((a, i) => {
                      const indx = getIndex();
                      return (
                        <AnimatedSpanEl
                          key={`items-item-${a}-${i}`}
                          ref={(el) => {
                            if (spanRef.current) {
                              spanRef.current[indx] = el;
                            }
                          }}
                        >
                          {a}&nbsp;
                        </AnimatedSpanEl>
                      );
                    })}
                  </div>
                }
                key={`items-item-${i}`}
                className={clsx(
                  "opacity-50",
                  {
                    "md:-rotate-8 md:translate-y-1/4 md:translate-x-1/12 -rotate-2":
                      i === 0,
                  },
                  { "md:rotate-6 md:-translate-y-1/6 rotate-2": i === 1 },
                  {
                    "md:-rotate-6 md:translate-y-1/6 md:-translate-x-1/12 -rotate-2":
                      i === 2,
                  },
                )}
              >
                {i === 0 && (
                  <div
                    ref={pointA1}
                    className="w-4 aspect-square rounded-full border border-[#4991b8] absolute right-1/4 top-6"
                  ></div>
                )}
                {i === 1 && (
                  <>
                    <div
                      ref={pointA2}
                      className="w-4 aspect-square rounded-full border border-[#4991b8] absolute left-1 top-6"
                    ></div>
                    <div
                      ref={pointB1}
                      className="w-4 aspect-square rounded-full border border-[#4991b8] absolute right-1/5 top-1/3"
                    ></div>
                  </>
                )}
                {i === 2 && (
                  <div
                    ref={pointB2}
                    className="w-4 aspect-square rounded-full border border-[#4991b8] absolute left-4 top-1/3"
                  ></div>
                )}
              </Card>
            );
          })}
          <div className="md:block">
            <SVGConnector
              ref={connectorA}
              containerRef={containerRef}
              fromRef={pointA1}
              toRef={pointA2}
              path={PATH_A}
              pathFrom={{ x: 10.716, y: 56.236 }} // start of PATH a
              pathTo={{ x: 233.016, y: 57.669 }} // end  of PATH a
              color="#4991b8"
            />
            <SVGConnector
              ref={connectorB}
              containerRef={containerRef}
              fromRef={pointB1}
              toRef={pointB2}
              path={PATH_B}
              pathFrom={{ x: 95.993, y: 11.704 }} // start of PATH_B
              pathTo={{ x: 92.134, y: 170.369 }} // end of PATH_B
              color="#4991b8"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OurProcess;
const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ index, title, description, children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          "p-1 md:p-2 relative rounded-4xl md:rounded-3xl w-full h-full bg-[#e0e0e0]/40 flex items-center font-inter justify-center",
          className,
        )}
      >
        <div
          className={clsx(
            "w-full rounded-3xl md:rounded-2xl relative px-4 md:px-6 h-full gap-4 md:gap-7 flex flex-col p-3 md:p-4 bg-[#e0e0e0]",
          )}
        >
          <h1 className="text-5xl md:text-7xl text-[#4991b8]">{index}</h1>
          <div className="flex flex-col gap-0.5 py-4 md:py-7">
            <h2 className="text-xl md:text-3xl text-[#111212]/85 leading-tight">
              {title}
            </h2>
            <h2 className="text-sm md:text-base text-[#111212]/60 leading-relaxed font-medium">
              {description}
            </h2>
          </div>
          {children}
        </div>
      </div>
    );
  },
);

const ourProcess: {
  title: string;
  index: number;
  description: string;
}[] = [
  {
    title: "Business Owners",
    description:
      "Sign solutions in any way, shape, or form for your business​.",
    index: 1,
  },
  {
    description: "Need a sign company or valued sign partner you can rely on?",
    index: 2,
    title: "Marketing Professionals",
  },
  {
    description: "Service, solutions, and years of knowledge.",
    index: 3,
    title: "Property Managers & Contractors",
  },
];
